import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select,
  Space, message, Card, Tag, DatePicker, Popconfirm
} from 'antd';
import { PlusOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getStudents, createStudent, updateStudent, deleteStudent,
} from '../api/students';
import { getBranches } from '../api/branches';
import BranchSelect from '../components/common/BranchSelect';
import UserSearchSelect from '../components/common/UserSearchSelect';
import SchoolSelect from '../components/common/SchoolSelect';
import useDebounce from '../hooks/useDebounce';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import useServerSort from '../hooks/useServerSort';
import useAuthStore from '../store/authStore';
import { apiErrorMessage } from '../utils/apiError';

const { Option } = Select;

const MANAGER_ROLES = ['developer_admin', 'super_admin', 'branch_manager'];
const SUPER_ROLES   = ['developer_admin', 'super_admin'];

const Students = () => {
  const { user } = useAuthStore();
  const isManager   = MANAGER_ROLES.includes(user?.role);
  const isSuperAdmin = SUPER_ROLES.includes(user?.role);
  const [students, setStudents] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [branchFilter, setBranchFilter] = useState(null);
  const [parentFilter, setParentFilter] = useState(null);
  const debouncedSearch = useDebounce(search, 400);
  const [form] = Form.useForm();
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('students', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();
  const navigate = useNavigate();
  const location = useLocation();

  const fetchStudents = async (page = 1, searchText = debouncedSearch, status = statusFilter, branch = branchFilter, perPage = pagination.pageSize, parentId = parentFilter, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const response = await getStudents({
        page,
        per_page: perPage,
        search: searchText,
        status,
        branch_id: branch,
        parent_id: parentId,
        sort_by: sortBy || undefined,
        sort_direction: sortDir || undefined,
      });
      setStudents(response.data.data);
      setPagination({
        current: response.data.current_page,
        pageSize: response.data.per_page,
        total: response.data.total,
      });
    } catch (error) {
      message.error('Öğrenciler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await getBranches({ per_page: 100 });
      setBranches(response.data.data);
    } catch (error) {
      console.error('Şubeler yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status') || null;
    setStatusFilter(statusParam);
    fetchStudents(1, debouncedSearch, statusParam, branchFilter);
  }, [location.search, debouncedSearch]);

  // Çift tıklama koruması: backend'de idempotency kontrolü yok, iki istek
  // iki öğrenci kaydı oluşturur. (Ödeme formlarında bu koruma zaten vardı.)
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      const data = {
        ...values,
        birth_date: values.birth_date ? values.birth_date.format('YYYY-MM-DD') : null,
      };
      await createStudent(data);
      message.success('Öğrenci oluşturuldu.');
      setModalOpen(false);
      form.resetFields();
      fetchStudents();
    } catch (error) {
      message.error(apiErrorMessage(error, 'İşlem başarısız.'));
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Ad Soyad',
      key: 'fullname',
      sorter: true,
      sortOrder: sortField === 'fullname' ? sortOrder : null,
      render: (_, record) => `${record.name} ${record.surname}`,
    },
    {
      title: 'TC Kimlik',
      dataIndex: 'tc_no',
      key: 'tc_no',
      sorter: true,
      sortOrder: sortField === 'tc_no' ? sortOrder : null,
    },
    {
      // Dogum tarihi `dayjs()` ile okunuyor, `.slice(0, 10)` ile DEGIL -
      // backend 'date:Y-m-d' cast'ine cevrildi ama dizeyi kesmek yine de
      // kirilgan bir okuma bicimi (bkz. backend/CLAUDE.md, tarih cast'leri).
      title: 'Doğum Tarihi',
      dataIndex: 'birth_date',
      key: 'birth_date',
      width: 130,
      sorter: true,
      sortOrder: sortField === 'birth_date' ? sortOrder : null,
      render: (v) => (v
        ? dayjs(v).format('DD.MM.YYYY')
        : <span className="text-gray-400">—</span>),
    },
    {
      title: 'Okul',
      dataIndex: 'school',
      key: 'school',
      sorter: true,
      sortOrder: sortField === 'school' ? sortOrder : null,
      // Katalogdaki resmî ad esas. Henüz bağlanmamış kayıtlarda ham metin
      // gösteriliyor ve işaretleniyor — Raporlar > Okullar'daki temizlik
      // listesiyle aynı kayıtlar (okul kataloğu taşıması, 2026-08-06).
      render: (v, record) => (record.school_record?.name
        ? record.school_record.name
        : v
          ? <span title="Okul listesine bağlanmamış kayıt" style={{ color: '#d46b08' }}>{v} ⚠</span>
          : <span className="text-gray-400">—</span>),
    },
    {
      title: 'Veli',
      key: 'parents',
      render: (_, record) => {
        const ps = record.parents || [];
        if (!ps.length) return <span className="text-gray-400">—</span>;
        if (!isManager) return ps.map(p => `${p.name} ${p.surname}`).join(', ');
        return ps.map((p, i) => (
          <React.Fragment key={p.id}>
            {i > 0 && ', '}
            <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${p.id}`)}>
              {p.name} {p.surname}
            </Button>
          </React.Fragment>
        ));
      },
    },
    {
      title: 'Şube',
      key: 'branch',
      render: (_, record) => record.branch?.name || '—',
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortOrder: sortField === 'status' ? sortOrder : null,
      render: (status, record) => (
        <Select
          value={status}
          size="small"
          style={{ width: 100 }}
          disabled={!isManager}
          onChange={async (value) => {
            try {
              await updateStudent(record.id, { status: value });
              message.success('Durum güncellendi.');
              fetchStudents();
            } catch (error) {
              message.error('Durum güncellenemedi.');
            }
          }}
        >
          <Option value="candidate">Aday</Option>
          <Option value="active">Aktif</Option>
          <Option value="passive">Pasif</Option>
        </Select>
      ),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => navigate(`/students/${record.id}`)}
            size="small"
          >
            Detay
          </Button>
          {isSuperAdmin && (
            <Popconfirm
              title="Öğrenciyi silmek istediğinize emin misiniz?"
              onConfirm={async () => {
                try {
                  await deleteStudent(record.id);
                  message.success('Öğrenci silindi.');
                  fetchStudents();
                } catch {
                  message.error('Öğrenci silinemedi.');
                }
              }}
              okText="Sil"
              cancelText="İptal"
              okButtonProps={{ danger: true }}
            >
              <Button icon={<DeleteOutlined />} danger size="small" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Öğrenci Yönetimi</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setModalOpen(true);
          }}
        >
          Yeni Öğrenci
        </Button>
      </div>

      <Card>
        <div className="flex gap-3 mb-4">
          <Input
            placeholder="Öğrenci adı, soyadı, TC veya okul ara..."
            allowClear
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <UserSearchSelect
            role="parent"
            allowClear
            style={{ width: 260 }}
            placeholder="Veli ara ve seç... (en az 2 harf)"
            value={parentFilter}
            renderLabel={(u) => `${u.name} ${u.surname}${u.branch?.name ? ` (${u.branch.name} - ${u.is_active ? 'Aktif' : 'Pasif'})` : ''}`}
            onChange={(value) => {
              setParentFilter(value || null);
              fetchStudents(1, debouncedSearch, statusFilter, branchFilter, pagination.pageSize, value || null);
            }}
          />
          <Select
            placeholder="Durum filtrele"
            allowClear
            style={{ width: 130 }}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              fetchStudents(1, debouncedSearch, value, branchFilter);
            }}
          >
            <Option value="candidate">Aday</Option>
            <Option value="active">Aktif</Option>
            <Option value="passive">Pasif</Option>
          </Select>
          <Select
            placeholder="Şube filtrele"
            allowClear
            style={{ width: 180 }}
            onChange={(value) => {
              setBranchFilter(value);
              fetchStudents(1, debouncedSearch, statusFilter, value);
            }}
          >
            {branches.map((branch) => (
              <Option key={branch.id} value={branch.id}>{branch.name}</Option>
            ))}
          </Select>
        </div>

        <Table
          columns={columns}
          dataSource={students}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo, _filters, sorter) => {
            const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
            setPersistedPageSize(paginationInfo.pageSize);
            fetchStudents(paginationInfo.current, debouncedSearch, statusFilter, branchFilter, paginationInfo.pageSize, parentFilter, sField, sDir);
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Toplam ${total} öğrenci`,
          }}
        />
      </Card>

      <Modal
        title="Yeni Öğrenci"
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <Form.Item name="name" label="Ad" rules={[{ required: true, message: 'Ad giriniz.' }]}>
              <Input placeholder="Ad" maxLength={100} />
            </Form.Item>
            <Form.Item name="surname" label="Soyad" rules={[{ required: true, message: 'Soyad giriniz.' }]}>
              <Input placeholder="Soyad" maxLength={100} />
            </Form.Item>
          </div>
          <Form.Item
            name="tc_no"
            label="TC Kimlik No"
            rules={[{ pattern: /^\d{11}$/, message: 'TC Kimlik 11 haneli olmalıdır.' }]}
          >
            <Input placeholder="11 haneli TC Kimlik No" maxLength={11} />
          </Form.Item>
          <Form.Item name="birth_date" label="Doğum Tarihi">
            <DatePicker className="w-full" format="DD.MM.YYYY" placeholder="Doğum tarihi seçiniz" />
          </Form.Item>
          {/* Serbest metin DEĞİL: okul katalogdan seçiliyor. Listede olmayan
              bir okulla karşılaşılırsa Okullar sayfasından eklenir. */}
          <Form.Item name="school_id" label="Okul">
            <SchoolSelect className="w-full" />
          </Form.Item>
          <Form.Item name="branch_id" label="Şube" rules={[{ required: true, message: 'Şube seçiniz.' }]}>
            <BranchSelect branches={branches} />
          </Form.Item>
          <Form.Item name="status" label="Durum" initialValue="candidate">
            <Select disabled={!isManager}>
              <Option value="candidate">Aday</Option>
              {isManager && <Option value="active">Aktif</Option>}
              {isManager && <Option value="passive">Pasif</Option>}
            </Select>
          </Form.Item>
          <Form.Item className="mb-0 flex justify-end">
            <Space>
              <Button onClick={() => { setModalOpen(false); form.resetFields(); }}>İptal</Button>
              <Button type="primary" htmlType="submit" loading={submitting}>Oluştur</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Students;